'use client';

import React, { useState } from 'react';
import Topbar from '@/components/Topbar';
import Footer from '@/components/Footer';
import Link from 'next/link';
import { Shield, CheckCircle, AlertTriangle, Lock, Eye, CreditCard, MessageSquare, UserCheck, Flag, ChevronDown, ChevronUp, Mail } from 'lucide-react';


const trustPillars = [
  {
    icon: UserCheck,
    title: 'Verified Sellers',
    color: 'text-amber-400',
    bg: 'bg-amber-400/10',
    description:
      'Sellers go through identity and payment verification via Stripe Connect before they can receive payouts. Verified badges are displayed on seller profiles and listings.',
  },
  {
    icon: Lock,
    title: 'Secure Payments',
    color: 'text-green-400',
    bg: 'bg-green-400/10',
    description:
      'All payments are processed by Stripe — a PCI DSS Level 1 certified provider. We never store your card details. Funds are held in escrow until you confirm receipt.',
  },
  {
    icon: Shield,
    title: 'Buyer Protection',
    color: 'text-blue-400',
    bg: 'bg-blue-400/10',
    description:
      'If an item doesn\'t arrive or isn\'t as described, raise a dispute within 7 days of the auto-release date. Our team reviews evidence from both parties and issues a fair resolution.',
  },
  {
    icon: MessageSquare,
    title: 'Encrypted Messaging',
    color: 'text-purple-400',
    bg: 'bg-purple-400/10',
    description:
      'All buyer–seller communication happens inside DrivenMarket\'s secure messaging system. Never share personal contact details before a transaction is complete.',
  },
  {
    icon: Eye,
    title: 'Listing Moderation',
    color: 'text-rose-400',
    bg: 'bg-rose-400/10',
    description:
      'Our team reviews flagged listings and removes anything that violates our guidelines — including counterfeit goods, stolen items, and listings that breach animal welfare law.',
  },
  {
    icon: CreditCard,
    title: 'Escrow & Payouts',
    color: 'text-cyan-400',
    bg: 'bg-cyan-400/10',
    description:
      'Seller payouts are only released after the buyer confirms receipt or the 5-day auto-release window closes. This protects both parties and prevents fraud.',
  },
];

const buyerTips = [
  'Always communicate through DrivenMarket\'s messaging system — never move to WhatsApp or email before completing a purchase.',
  'Check the seller\'s profile: look for the verified badge, review history, and how long they\'ve been on the platform.',
  'Read the listing description carefully. If something is unclear, ask the seller before buying.',
  'Never pay via bank transfer, PayPal Friends & Family, or cash. Only use DrivenMarket\'s secure checkout.',
  'Inspect items promptly on arrival. You have 7 days from auto-release to raise a dispute.',
  'If a deal seems too good to be true — it probably is. Report suspicious listings immediately.',
  'For high-value items, ask the seller for additional photos or a short video before purchasing.',
];

const sellerTips = [
  'Complete your Stripe Connect onboarding to receive payouts. Unverified sellers cannot withdraw funds.',
  'Use accurate, detailed descriptions and real photos of the actual item — not stock images.',
  'Respond to buyer messages promptly. Slow responses lead to disputes and negative reviews.',
  'Ship items within the timeframe stated in your listing. Update the tracking number as soon as available.',
  'Do not attempt to move transactions off-platform. This voids buyer protection and may result in account suspension.',
  'Disclose any defects, wear, or damage honestly. Misrepresentation is grounds for a full refund.',
  'Keep proof of postage. In the event of a dispute, tracking evidence is essential.',
];

const redFlags = [
  { flag: 'Seller asks you to pay outside the platform', risk: 'High' },
  { flag: 'Price is significantly below market value', risk: 'High' },
  { flag: 'Seller pressures you to act quickly', risk: 'High' },
  { flag: 'No reviews and recently created account', risk: 'Medium' },
  { flag: 'Stock photos instead of real item photos', risk: 'Medium' },
  { flag: 'Vague or copy-pasted description', risk: 'Medium' },
  { flag: 'Seller refuses to answer specific questions', risk: 'Medium' },
  { flag: 'Requests personal contact details before purchase', risk: 'Medium' },
];

const faqs = [
  {
    q: 'What happens if my item doesn\'t arrive?',
    a: 'Raise a dispute from your Order History within 7 days of the auto-release date. Our team will review tracking information and evidence from both parties. If the item hasn\'t been delivered, you\'ll receive a full refund.',
  },
  {
    q: 'What if the item is not as described?',
    a: 'Open a dispute within 7 days of receiving the item. Provide photos and a clear description of how the item differs from the listing. We\'ll review the evidence and issue a resolution — which may include a full or partial refund.',
  },
  {
    q: 'Is my payment information secure?',
    a: 'Yes. All payments are handled by Stripe, which is PCI DSS Level 1 compliant — the highest level of payment security. DrivenMarket never sees or stores your card details.',
  },
  {
    q: 'How do I know if a seller is legitimate?',
    a: 'Look for the verified badge on their profile. Check their review history and how long they\'ve been on the platform. If in doubt, ask questions via our messaging system before purchasing.',
  },
  {
    q: 'Can I meet a seller in person?',
    a: 'For local collection, you can arrange this through our messaging system. Always meet in a public place, bring a friend if possible, and inspect the item before handing over any payment. Complete the transaction through DrivenMarket even for in-person collections.',
  },
  {
    q: 'What is Lucy\'s Law and how does it apply here?',
    a: 'Lucy\'s Law (2020) bans the sale of puppies and kittens by pet shops and third-party commercial dealers in England. All puppy and dog listings on DrivenMarket must come directly from the breeder. We display a compliance notice on all relevant listings and require breeders to confirm they are the original breeder.',
  },
  {
    q: 'How do I report a suspicious listing or user?',
    a: 'Use the "Report" button on any listing, or visit our Report a Listing page. For urgent safety concerns, email trust@drivenmarket.co.uk directly.',
  },
];

export default function SafetyPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <div className="min-h-screen bg-[hsl(222,47%,6%)]">
      <Topbar />
      <main className="pt-16">
        {/* Hero */}
        <div className="bg-gradient-to-b from-[hsl(222,47%,8%)] to-[hsl(222,47%,6%)] border-b border-[hsl(222,25%,18%)]">
          <div className="max-w-4xl mx-auto px-4 lg:px-8 py-14 text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-amber-400/10 mb-5">
              <Shield className="w-7 h-7 text-amber-400" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-3">Safety &amp; Trust</h1>
            <p className="text-slate-400 text-base leading-relaxed max-w-2xl mx-auto">
              DrivenMarket is built for serious handlers. That means we take the safety of every transaction — and every animal — seriously. Here's how we protect you.
            </p>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 lg:px-8 py-12 space-y-14">

          {/* Trust Pillars */}
          <section>
            <h2 className="text-xl font-bold text-white mb-6">How We Keep You Safe</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {trustPillars?.map((pillar) => {
                const PillarIcon = pillar?.icon;
                return (
                  <div key={pillar?.title} className="bg-[hsl(222,40%,9%)] border border-[hsl(222,25%,18%)] rounded-xl p-5">
                    <div className={`inline-flex items-center justify-center w-10 h-10 rounded-lg ${pillar?.bg} mb-4`}>
                      <PillarIcon className={`w-5 h-5 ${pillar?.color}`} />
                    </div>
                    <h3 className="text-sm font-semibold text-white mb-2">{pillar?.title}</h3>
                    <p className="text-slate-400 text-xs leading-relaxed">{pillar?.description}</p>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Buyer Tips */}
          <section>
            <h2 className="text-xl font-bold text-white mb-2">Buyer Safety Tips</h2>
            <p className="text-slate-400 text-sm mb-5">Follow these guidelines to protect yourself on every purchase.</p>
            <div className="bg-[hsl(222,40%,9%)] border border-[hsl(222,25%,18%)] rounded-xl divide-y divide-[hsl(222,25%,18%)]">
              {buyerTips?.map((tip, i) => (
                <div key={i} className="flex items-start gap-3 p-4">
                  <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                  <p className="text-slate-300 text-sm leading-relaxed">{tip}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Seller Tips */}
          <section>
            <h2 className="text-xl font-bold text-white mb-2">Seller Safety Tips</h2>
            <p className="text-slate-400 text-sm mb-5">Protect your account and your buyers by following these best practices.</p>
            <div className="bg-[hsl(222,40%,9%)] border border-[hsl(222,25%,18%)] rounded-xl divide-y divide-[hsl(222,25%,18%)]">
              {sellerTips?.map((tip, i) => (
                <div key={i} className="flex items-start gap-3 p-4">
                  <CheckCircle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                  <p className="text-slate-300 text-sm leading-relaxed">{tip}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Red Flags */}
          <section>
            <h2 className="text-xl font-bold text-white mb-2">Red Flags to Watch For</h2>
            <p className="text-slate-400 text-sm mb-5">If you notice any of the following, proceed with caution or report the listing.</p>
            <div className="bg-[hsl(222,40%,9%)] border border-[hsl(222,25%,18%)] rounded-xl overflow-hidden">
              <div className="hidden sm:grid sm:grid-cols-[1fr_auto] text-xs font-semibold uppercase tracking-widest text-slate-500 px-4 py-3 border-b border-[hsl(222,25%,18%)]">
                <span>Warning Sign</span>
                <span>Risk Level</span>
              </div>
              {redFlags?.map((item, i) => (
                <div key={i} className="flex items-start justify-between gap-3 px-4 py-3 border-b border-[hsl(222,25%,18%)] last:border-0">
                  <div className="flex items-start gap-2 flex-1 min-w-0">
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-300 text-sm leading-snug">{item?.flag}</span>
                  </div>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${item?.risk === 'High' ? 'bg-red-500/15 text-red-400' : 'bg-amber-500/15 text-amber-400'}`}>
                    {item?.risk}
                  </span>
                </div>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section>
            <h2 className="text-xl font-bold text-white mb-6">Frequently Asked Questions</h2>
            <div className="space-y-2">
              {faqs?.map((faq, i) => (
                <div key={i} className="bg-[hsl(222,40%,9%)] border border-[hsl(222,25%,18%)] rounded-xl overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
                  >
                    <span className="text-sm font-medium text-white">{faq?.q}</span>
                    {openFaq === i ? (
                      <ChevronUp className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    )}
                  </button>
                  {openFaq === i && (
                    <div className="px-5 pb-4 border-t border-[hsl(222,25%,18%)] pt-4">
                      <p className="text-slate-400 text-sm leading-relaxed">{faq?.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* Contact & Report CTA */}
          <section className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-[hsl(222,40%,9%)] border border-[hsl(222,25%,18%)] rounded-xl p-6">
              <Flag className="w-5 h-5 text-rose-400 mb-3" />
              <h3 className="text-sm font-semibold text-white mb-2">Report a Listing</h3>
              <p className="text-slate-400 text-xs leading-relaxed mb-4">Seen something suspicious? Report it and our team will investigate within 24 hours.</p>
              <Link href="/report" className="inline-flex items-center gap-2 text-xs font-semibold text-rose-400 hover:text-rose-300 transition-colors">
                Report a Listing →
              </Link>
            </div>
            <div className="bg-[hsl(222,40%,9%)] border border-[hsl(222,25%,18%)] rounded-xl p-6">
              <Mail className="w-5 h-5 text-amber-400 mb-3" />
              <h3 className="text-sm font-semibold text-white mb-2">Contact Trust &amp; Safety</h3>
              <p className="text-slate-400 text-xs leading-relaxed mb-4">For urgent safety concerns, reach our dedicated trust team directly.</p>
              <a href="mailto:trust@drivenmarket.co.uk" className="inline-flex items-center gap-2 text-xs font-semibold text-amber-400 hover:text-amber-300 transition-colors">
                trust@drivenmarket.co.uk →
              </a>
            </div>
          </section>

        </div>
      </main>
      <Footer />
    </div>
  );
}
